import { useUserStore } from '@/stores/user';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { refreshAccessToken } from '@/services/authRefresh';

// Set by the session module at startup (registerSessionHandlers in
// inactivityTimeOut.ts). Injected rather than imported, because the session
// module itself calls the API through this instance.
type SessionHandlers = {
  onActivity: () => void;
  onRefreshFailed: () => Promise<void>;
};

let sessionHandlers: SessionHandlers = {
  onActivity: () => undefined,
  onRefreshFailed: () => Promise.resolve(),
};

export const setSessionHandlers = (handlers: SessionHandlers) => {
  sessionHandlers = handlers;
};

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = useUserStore.getState().accessToken;
    if (token) {
      config.headers = config.headers || {};
      (config.headers as any).Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    sessionHandlers.onActivity();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (
      error?.response?.status === 401 &&
      originalRequest &&
      !originalRequest?._retry &&
      !originalRequest.url?.includes('/auth/')
    ) {
      originalRequest._retry = true;
      // Read now, not from the snapshot taken at import: the session may have ended since.
      const { refreshToken } = useUserStore.getState();
      if (!refreshToken) return Promise.reject(error);
      try {
        const newAccessToken = await refreshAccessToken(refreshToken);
        // Signed out while refreshing: don't bring the token back.
        if (useUserStore.getState().refreshToken !== refreshToken) return Promise.reject(error);

        useUserStore.setState({ accessToken: newAccessToken });

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        sessionHandlers.onActivity();
        return axios(originalRequest);
      } catch (refreshError) {
        // Awaited: the full page load below would cancel the logout request.
        await sessionHandlers.onRefreshFailed();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

const get = <T>(url: string, config?: AxiosRequestConfig) => api.get<T>(url, config);
const post = <T>(url: string, data?: any, config?: AxiosRequestConfig) => api.post<T>(url, data, config);
const put = <T>(url: string, data?: any, config?: AxiosRequestConfig) => api.put<T>(url, data, config);
const patch = <T>(url: string, data?: any, config?: AxiosRequestConfig) => api.patch<T>(url, data, config);
const del = <T>(url: string, config?: AxiosRequestConfig) => api.delete<T>(url, config);

export default {
  api,
  get,
  post,
  put,
  patch,
  delete: del,
};
